import { extractChain, parseEther, type Address } from 'viem'
import type { Chain } from 'viem'
import * as _chains from './chains/index.js'
import { getExchangeRate } from '../utils/index.js'
import { PAYING_TOKENS } from './tokens/index.js'
import { CrossmintRedirectParamsSchema } from './schema/index.js'
import { z } from 'zod'
import type { BlockchainKeys } from './types/index.js'
import { Network } from '../consts/crypto/index.js'

const chains: Chain[] = Object.values(_chains)
type ChainId = (typeof chains)[number]['id']

export function blockExplorer(chainId: number | ChainId) {
  const { blockExplorers } = extractChain({
    chains,
    id: Number(chainId) as ChainId,
  })

  return blockExplorers?.default.url || undefined
}

export function marketExplorer(params: {
  chainId: number | ChainId
  address: Address
  tokenId: number
}) {
  const { chainId, address, tokenId } = params
  const { nativeCurrency } = extractChain({
    chains,
    id: chainId,
  })
  const type = typeChain(chainId)

  const symbol = nativeCurrency?.symbol
  const testChainsOpensea: Record<number, string> = {
    11155111: 'sepolia',
    80002: 'amoy',
    81457: 'blast',
    8453: 'base',
  }

  switch (chainId) {
    case 1: // Ethereum Mainnet
      return `https://opensea.io/assets/ethereum/${address}/${tokenId}`

    case 137: // Polygon Mainnet
      return `https://opensea.io/assets/matic/${address}/${tokenId}`

    case 42220: // Celo Mainnet
      return `https://explorer.celo.org/mainnet/token/${address}/instance/${tokenId}/token-transfers`

    case 69001: // Sui Mainnet
      return `https://suiscan.xyz/mainnet/object/${address}/`

    case 69000: // Sui Testnet
      return `https://suiscan.xyz/testnet/object/${address}/`

    default:
      if (testChainsOpensea[chainId]) {
        return `https://testnets.opensea.io/assets/${testChainsOpensea[chainId]}/${address}/${tokenId}`
      } else if (symbol === 'sFUEL') {
        return `${type === 'mainnet' ? 'https://honorable-steel-rasalhague' : 'https://giant-half-dual-testnet'}.explorer.${type}.skalenodes.com/token/${address}/instance/${tokenId}`
      }
      return undefined
  }
}

export function validatePayload(params: any) {
  try {
    return CrossmintRedirectParamsSchema.parse(params)
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('Validation failed:', error.errors)
      throw new Error('Invalid payload')
    }
    throw error
  }
}

export function truncateAddress(address: string | Address, start = 4, end = 6) {
  if (address.length < start + end + 1) {
    return address
  }

  const result =
    address.substring(0, start) +
    '\u2026' +
    address.substring(address.length - end)

  return result
}

export function formattingPrice(
  chainId: number,
  payingToken: string,
  price: number
) {
  if (symbolPayingToken(chainId, payingToken) === 'USDC') {
    return BigInt(price * 1000000)
  }
  return parseEther(`${price}`)
}

export function symbolChain(chainId: number | ChainId) {
  const { nativeCurrency } = extractChain({
    chains,
    id: Number(chainId) as ChainId,
  })

  return nativeCurrency.symbol
}

export function chainName(chainId: number | ChainId) {
  const { name } = extractChain({
    chains,
    id: chainId as ChainId,
  })

  return name
}
export function network(chainId: number | ChainId) {
  if (symbolChain(chainId) === 'SUI') {
    return Network.SUI
  } else {
    return Network.ETHEREUM
  }
}

export function blockChainName(chainId: number) {
  const name = chainName(chainId) as string
  if (symbolChain(chainId) === 'ETH') {
    return 'ethereum'
  }
  return name?.split(' ')[0]?.toLocaleLowerCase() as BlockchainKeys
}

export function chainForCrossmint(chainId: number) {
  const network = chainName(chainId)?.toLocaleLowerCase()

  const blockChain =
    symbolChain(chainId) === 'ETH' ? 'ethereum' : network.toLocaleLowerCase()

  const formattedBlockChain =
    blockChain.split(' ').length === 1
      ? blockChain
      : blockChain.split(' ').join('-')

  return blockChain === network
    ? `${formattedBlockChain}`
    : `${blockChain}-${network}`
}

export function symbolPayingToken(chainId: number, token: string) {
  const currentNetworkTokens = new Map(
    Object.entries(PAYING_TOKENS[chainId] || {})
  )

  for (const [key, item] of currentNetworkTokens.entries()) {
    if (item.toLocaleLowerCase() === token.toLocaleLowerCase()) {
      return key
    }
  }

  return undefined
}

export function networkName(chainId: number | ChainId) {
  const { name } = extractChain({
    chains,
    id: chainId as ChainId,
  })

  return name
}
export function isStableCoin(chainId: number, token: string) {
  const symbolToken = symbolPayingToken(chainId, token)
  const symbol = symbolChain(chainId)
  if (symbol !== symbolToken) return true
  return false
}
export function getSuiChainId(chain: string) {
  const isTestnet = chain.toLowerCase() === 'testnet'
  const chainMatch = chains.find(
    (c) =>
      c.name.toLowerCase().includes(Network.SUI.toLocaleLowerCase()) &&
      c.testnet === isTestnet
  )
  return chainMatch?.id
}

interface ExchangeRate {
  [token: string]: number | null
}

interface ExchangeRates {
  [currency: string]: ExchangeRate
}

export async function useExchange(
  initialCurrencies: string[],
  initialTokens: string[]
) {
  let exchangeData: ExchangeRates = {}
  const currencies = initialCurrencies
  const tokens = initialTokens

  async function fetchExchangeRates(
    newInitialCurrencies: string[] = currencies,
    newInitialTokens: string[] = tokens
  ) {
    try {
      const exchange = await getExchangeRate(
        newInitialCurrencies,
        newInitialTokens
      )
      if (exchange) {
        exchangeData = exchange
      }
    } catch (error) {
      console.error('Error fetching and storing exchange rates:', error)
    }
  }

  function exchangeCryptoPrice(
    value: number,
    currency: string,
    token: string
  ): number {
    const currencyData = exchangeData[currency]

    if (
      !currencyData ||
      currencyData[token] === undefined ||
      currencyData[token] === null
    ) {
      console.error('Exchange rate not found for the given currency')
      return 0
    }

    const rate = 1 / (currencyData[token] || 1)
    return Number((value * rate).toFixed(4))
  }

  await fetchExchangeRates()

  return {
    exchangeCryptoPrice,
    fetchExchangeRates,
    exchangeData,
  }
}

export function typeChain(chainId: number) {
  const chain = extractChain({
    chains,
    id: chainId as ChainId,
  })

  return chain?.testnet === false || chain?.testnet === undefined
    ? 'mainnet'
    : 'testnet'
}
